import { useMemo } from 'react';
import { useSetAtom } from 'jotai';
import { Editor, NodeViewProps } from '@tiptap/react';

import { useBlock, useBlockPatch, useNote, useResolvedInstructionIds } from '@/hooks/rxdb-models';

import { sidebarOverlayAtom } from '@/state';
import { copyToClipboard } from '@/utils/helper-utils';
import { toTiptapHtml } from '@flintmint/core-shared/utils';
import { cleanHeadingAttributes, generateBlockNode, getBlockNodeContent } from '@/utils/editor-utils';

import type { BlockContext } from '@/constants/types';
import type { Block } from '@flintmint/core-shared/rxdb';

export const useBlockInstanceActions = (nodeViewProps: NodeViewProps): BlockInstanceActions => {
  const { getPos, node, editor, deleteNode } = nodeViewProps;

  const setSidebarOverlay = useSetAtom(sidebarOverlayAtom);

  const { id, instanceId } = node.attrs;

  const { data: masterBlock } = useBlock(id);
  const { mutate: patchBlock } = useBlockPatch();
  const { data: note } = useNote(editor.storage.documentInfo.documentId);

  // Read on use, never cached: editing the note above a block instance moves it without
  // re-rendering its node view, and every command below silently no-ops on a position that no
  // longer holds the block.
  const currentPos = () => getPos() ?? 0;

  const blockInstructions = note?.blockInstructions?.[id];
  const instructionIds = useMemo(() => Object.keys(blockInstructions || {}), [blockInstructions]);
  // Soft refs: while resolving, useResolvedInstructionIds falls back to the raw ids, so the
  // filter below computes over all map values same as once resolved — a dangling id can't
  // mislabel an otherwise all-enabled block as partially enabled either way.
  const resolvedInstructionIds = useResolvedInstructionIds(instructionIds);

  const blockInstructionStatus = useMemo((): BlockInstructionStatus | null => {
    if (!note || !masterBlock) {
      return null;
    }

    const resolvedIds = new Set(resolvedInstructionIds);
    const statuses = Object.entries(blockInstructions || {})
      .filter(([instructionId]) => resolvedIds.has(instructionId))
      .map(([, enabled]) => enabled);

    if (!statuses.length) {
      return null;
    }

    const enabledCount = statuses.filter(Boolean).length;

    if (enabledCount === statuses.length) {
      return {
        status: 'all-enabled',
        icon: '3rd-bracket-solid',
        message: 'Enabled All Instructions',
      };
    }

    if (!enabledCount) {
      return {
        status: 'all-disabled',
        icon: '3rd-bracket-crossed',
        message: 'Disabled All Instructions',
      };
    }

    return {
      icon: '3rd-bracket-bulk',
      status: 'partially-enabled',
      message: 'Partially Enabled Instructions',
    };
  }, [note, masterBlock, resolvedInstructionIds, blockInstructions]);

  const isOutOfSync = useMemo(() => {
    if (masterBlock && !masterBlock.archived) {
      const { blockHtml } = getBlockNodeContent(editor as Editor, node);
      const masterRaw = masterBlock.content ?? '';
      const masterNorm = toTiptapHtml(cleanHeadingAttributes(masterRaw));
      const instanceNorm = toTiptapHtml(cleanHeadingAttributes(blockHtml));
      const nameMismatch = masterBlock.name !== node.attrs.name;
      const contentMismatch = masterNorm !== instanceNorm;

      return contentMismatch || nameMismatch;
    }

    return false;
  }, [masterBlock, editor, node]);

  const getBlockContext = (extra?: Partial<BlockContext>) =>
    note && {
      context: {
        note,
        noteId: note.id,
        position: getPos(),
        editor: editor as Editor,
        blockInstanceId: instanceId,
        ...extra,
      },
    };

  const editMasterBlock = () => {
    setSidebarOverlay({
      blockId: id,
      type: 'blocks',
      ...getBlockContext(),
    });
  };

  const createNewBlockFromExisting = () => {
    const { blockHtml } = getBlockNodeContent(editor as Editor, node);

    setSidebarOverlay({
      type: 'blocks',
      draftId: crypto.randomUUID(),
      ...getBlockContext({
        initialContent: blockHtml,
        initialName: node.attrs.name,
      }),
    });
  };

  const deleteBlockNode = () => {
    deleteNode();
    editor.commands.cleanupBlockStorage();
  };

  const copyBlockContents = () => {
    const { blockHtml, blockText } = getBlockNodeContent(editor as Editor, node);
    return copyToClipboard({ content: blockHtml, textContent: blockText });
  };

  const convertBlockToText = () => {
    editor.commands.convertBlockToText(instanceId, currentPos(), node.content);
  };

  const pushBlockChangesToMaster = () => {
    const { blockHtml } = getBlockNodeContent(editor as Editor, node);

    const newBlockNode = generateBlockNode({ ...masterBlock, content: blockHtml } as Block, editor.schema, instanceId);
    editor.commands.positionCursorAfterBlockUpdate(currentPos(), editor.state.selection.from, newBlockNode);

    return patchBlock(masterBlock!, {
      content: blockHtml,
    });
  };

  const revertToMasterBlock = () => {
    if (masterBlock) {
      editor.commands.replaceBlockWithNew(instanceId, currentPos(), masterBlock);
    }
  };

  const copyBlock = () => {
    editor.commands.copyBlock(currentPos(), node);
  };

  return {
    copyBlock,
    isOutOfSync,
    deleteBlockNode,
    editMasterBlock,
    copyBlockContents,
    convertBlockToText,
    revertToMasterBlock,
    blockInstructionStatus,
    pushBlockChangesToMaster,
    createNewBlockFromExisting,
    hasMasterBlock: !!masterBlock && !masterBlock.archived,
  };
};

interface BlockInstanceActions {
  isOutOfSync: boolean;
  copyBlock: () => void;
  hasMasterBlock: boolean;
  deleteBlockNode: () => void;
  editMasterBlock: () => void;
  copyBlockContents: () => void;
  convertBlockToText: () => void;
  revertToMasterBlock: () => void;
  pushBlockChangesToMaster: () => void;
  createNewBlockFromExisting: () => void;
  blockInstructionStatus: BlockInstructionStatus | null;
}

export type BlockInstructionStatus = { status: string; icon: string; message: string };
